import { useMutation, useQuery } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";

import type { TimetableLessonView } from "../../api/statelessClient";
import { getProjectRepository } from "../../data/projectRepository";
import { ResourceRoomProjectHeader } from "../shared/ResourceRoomProjectHeader";
import "../shared/resource-room-workspace.css";

type View = "ROOM" | "TEACHER" | "STUDENT" | "CLASS";
const viewLabels: Record<View, string> = {
  ROOM: "教室课表",
  TEACHER: "教师课表",
  STUDENT: "学生周课表",
  CLASS: "班主任抽离汇总"
};

function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  document.body.append(anchor);
  anchor.click();
  anchor.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 0);
}

export function ResourceRoomTimetablePage({ projectId }: { projectId: string }): React.JSX.Element {
  const [view, setView] = useState<View>("ROOM");
  const [parity, setParity] = useState<"ODD" | "EVEN">("ODD");
  const [objectId, setObjectId] = useState("");
  const [query, setQuery] = useState("");
  const [hideStudentNames, setHideStudentNames] = useState(false);
  const [message, setMessage] = useState("");
  const documentQuery = useQuery({ queryKey: ["resource-room-document", projectId], queryFn: async () => (await getProjectRepository()).readDocument(projectId) });
  const timetable = useQuery({
    queryKey: ["resource-room-timetable", projectId, view, parity],
    queryFn: async () => (await getProjectRepository()).viewTimetable(projectId, { view, week_parity: parity }),
    enabled: Boolean(documentQuery.data)
  });
  useEffect(() => {
    setObjectId("");
    setQuery("");
  }, [view]);
  useEffect(() => {
    const options = timetable.data?.schedules ?? [];
    if (!options.length) return;
    setObjectId((current) => {
      if (current === "ALL" && view !== "TEACHER") return current;
      return options.some((item) => item.object_id === current)
        ? current
        : options[0].object_id;
    });
  }, [timetable.data, view]);
  const exportMutation = useMutation({
    mutationFn: async (scope: "CURRENT" | "COMPLETE") => (await getProjectRepository()).export(projectId, {
      view,
      scope,
      object_id: scope === "CURRENT" ? objectId : null,
      hide_student_names: hideStudentNames
    }),
    onSuccess: ({ blob, filename }) => { downloadBlob(blob, filename); setMessage("Excel 已生成并开始下载"); },
    onError: (error: unknown) => setMessage(error instanceof Error ? error.message : "导出失败")
  });
  const schedules = useMemo(() => {
    const normalized = query.trim().toLocaleLowerCase();
    if (view === "TEACHER" && !objectId) return [];
    return (timetable.data?.schedules ?? []).filter((item) => (objectId === "ALL" || item.object_id === objectId) && (!normalized || item.object_label.toLocaleLowerCase().includes(normalized)));
  }, [timetable.data, objectId, query, view]);
  const lessons = schedules.flatMap((item) => item.lessons);
  const uniqueLessons = Array.from(new Map(lessons.map((lesson) => [lesson.session_id, lesson])).values());
  const slots = timetable.data?.time_slots ?? [];
  const lessonsAt = (weekday: number, slotId: string): TimetableLessonView[] => uniqueLessons.filter((item) => item.weekday === weekday && item.time_slot_ids.includes(slotId));
  if (!documentQuery.data) return <main className="page-shell"><p className="state-message">正在读取课表…</p></main>;
  return (
    <main className="page-shell page-shell-wide">
      <ResourceRoomProjectHeader
        projectId={projectId}
        eyebrow="阶段 4 · 课表与导出"
        title="课表与导出"
      />
      <section className="resource-panel">
        <nav className="resource-tabs" aria-label="课表视角">{(Object.keys(viewLabels) as View[]).map((item) => <button className={view === item ? "active" : ""} type="button" key={item} onClick={() => setView(item)}>{viewLabels[item]}</button>)}</nav>
        <div className="resource-batch-bar">
          <label className="field"><span>周次</span><select value={parity} onChange={(event) => setParity(event.target.value as typeof parity)}><option value="ODD">单周</option><option value="EVEN">双周</option></select></label>
          <label className="field"><span>{viewLabels[view]}</span><select value={objectId} onChange={(event) => setObjectId(event.target.value)}>{view === "TEACHER" ? <option value="">请选择教师</option> : <option value="ALL">{view === "ROOM" ? "全部教室（整合）" : "全部对象"}</option>}{(timetable.data?.schedules ?? []).map((item) => <option key={item.object_id} value={item.object_id}>{item.object_label}</option>)}</select></label>
          <label className="field"><span>搜索</span><input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="姓名、班级或教室" /></label>
          <label className="resource-privacy-option"><input type="checkbox" checked={hideStudentNames} onChange={(event) => setHideStudentNames(event.target.checked)} /><span>隐藏学生姓名（隐私版）</span></label>
          <button className="secondary-button" type="button" disabled={!objectId || objectId === "ALL" || exportMutation.isPending} onClick={() => exportMutation.mutate("CURRENT")}>导出当前对象</button>
          <button className="primary-button" type="button" disabled={exportMutation.isPending} onClick={() => exportMutation.mutate("COMPLETE")}>导出完整课表</button>
        </div>
        {message ? <p className="resource-success" role="status">{message}</p> : null}
        {timetable.isLoading ? <p className="state-message">正在生成课表视图…</p> : null}
        {!timetable.isLoading && !uniqueLessons.length ? <div className="empty-card compact"><h2>{view === "TEACHER" && !objectId ? "请选择教师" : "暂无已排课程"}</h2></div> : null}
        {uniqueLessons.length ? <div className="resource-table-scroll" tabIndex={0} aria-label={`${viewLabels[view]}，可横向滚动查看更多`}><div className="resource-schedule-grid"><div className="resource-grid-heading">节次</div>{[1, 2, 3, 4, 5].map((day) => <div className="resource-grid-heading" key={day}>星期{"一二三四五"[day - 1]}</div>)}{slots.flatMap((slot) => [<div className="resource-grid-heading" key={`${slot.id}-label`}>{slot.raw_label}</div>, ...[1, 2, 3, 4, 5].map((day) => <div key={`${slot.id}-${day}`}>{lessonsAt(day, slot.id).map((lesson) => <article className="resource-lesson-card" key={`${lesson.session_id}-${slot.id}`}><strong>{lesson.intervention_course_name}</strong> · {lesson.lesson_type === "INDIVIDUAL" ? "个训" : "小组"}<br />{lesson.student_names.join("、")}<br />{lesson.room_name} · {lesson.main_teacher_name}{lesson.assistant_teacher_names.length ? ` + ${lesson.assistant_teacher_names.join("、")}` : ""}{view === "STUDENT" || view === "CLASS" ? <><br />原班：{lesson.original_course_names.join("；")}</> : null}{view === "TEACHER" && lesson.teacher_role ? <><br />本人角色：{lesson.teacher_role === "MAIN" ? "主教" : "助教"}</> : null}</article>)}</div>)] )}</div></div> : null}
      </section>
    </main>
  );
}
